export const sanitizeTextInput = (input, disallowedRegex = /[@#$%^&*_=+<>[\]{}|\\]/) => {
    return input
        .split('')
        .filter(char => !disallowedRegex.test(char))
        .join('')
        .trimStart();
};

/**
 * Validates and filters input based on the specified option.
 * @param {string} value - The input value to validate.
 * @param {string} option - The validation type ('text', 'numeric', 'email', 'mobile').
 * @param {number} [limit] - Optional length limit.
 * @returns {string} - The filtered value.
 */
export const validateValue = (value, option, limit = null) => {
    if (!value) return "";

    let result = value;

    switch (option) {
        case 'text':
            // Only alphabets and spaces
            result = value.replace(/[^a-zA-Z\s]/g, '');
            break;
        case 'email':
            // Only alphabets, numbers, @, -, ., and _
            result = value.replace(/[^a-zA-Z0-9@\-._]/g, '');
            break;
        case 'numeric':
        case 'mobile':
            // Only numbers
            result = value.replace(/[^0-9]/g, '');
            break;
        default:
            result = value;
            break;
    }

    result = result.trimStart();
    if (limit) {
        result = result.slice(0, limit);
    }

    return result;
};

/**
 * Sanitizes text input while preserving HTML content
 * If the content contains HTML tags, it preserves the HTML (will be sanitized later by DOMPurify)
 * If the content is plain text, it applies sanitizeTextInput to remove special characters
 * @param {string} text - The text to sanitize
 * @returns {string} - The sanitized text (HTML preserved if present, otherwise sanitized)
 */
export const sanitizeTextPreservingHTML = (text) => {
    if (!text) return text;

    const trimmedText = text.trim();

    // Check if content contains HTML tags
    const hasHTML = /<[^>]+>/.test(trimmedText);

    // If HTML is present, preserve it (it will be sanitized by DOMPurify in getSanitizedHTML)
    // If plain text, apply sanitizeTextInput to remove special characters
    return hasHTML ? trimmedText : sanitizeTextInput(trimmedText);
};

/**
 * Converts an HTML string to readable plain text.
 * - Preserves line breaks for block-level elements (p, div, tr)
 * - Separates table columns with tabs for a readable layout
 * - Collapses excessive blank lines
 *
 * @param {string} html - Raw HTML string (e.g. from RichInputBox contenteditable)
 * @returns {string} - Clean plain text suitable for clipboard or plain-text contexts
 */
export const getPlainTextFromHtml = (html) => {
    if (!html) return "";
    try {
        const div = document.createElement("div");
        div.innerHTML = html;

        // Process tables specifically to ensure tabular format
        const tables = div.querySelectorAll("table");
        tables.forEach(table => {
            const rows = table.querySelectorAll("tr");
            rows.forEach(row => {
                const cells = row.querySelectorAll("td, th");
                cells.forEach((cell, index) => {
                    // Add tab between cells, but not after the last cell
                    if (index < cells.length - 1) {
                        cell.insertAdjacentText("afterend", "\t");
                    }
                });
                // Add newline after each row
                row.insertAdjacentText("afterend", "\n");
            });
            // Add a newline after the table
            table.insertAdjacentText("afterend", "\n");
        });

        // Handle other block elements and line breaks
        div.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
        div.querySelectorAll("p, div").forEach((el) => {
            // Only add newline if it's not already inside a table we processed
            if (!el.closest("table")) {
                el.insertAdjacentText("afterend", "\n");
            }
        });

        // Get text content and clean up
        let text = div.textContent || "";
        
        // Final cleanup: collapse multiple newlines and trim
        return text.replace(/\n{3,}/g, "\n\n").replace(/[ \t]+\n/g, "\n").trim();
    } catch (err) {
        console.error("getPlainTextFromHtml error:", err);
        return html;
    }
};

/**
 * Copies rich text (HTML + Plain Text) to the clipboard.
 * 
 * @param {string} plainText - The plain text version of the content.
 * @param {string} htmlContent - The HTML version of the content (optional).
 * @returns {Promise<boolean>} - Resolves to true if successful, false otherwise.
 */
export const copyRichTextToClipboard = async (plainText, htmlContent = "") => {
    try {
        if (typeof ClipboardItem !== "undefined" && navigator.clipboard && htmlContent) {
            const clipboardItem = new ClipboardItem({
                "text/plain": new Blob([plainText], { type: "text/plain" }),
                "text/html": new Blob([htmlContent], { type: "text/html" }),
            });
            await navigator.clipboard.write([clipboardItem]);
            return true;
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(plainText);
            return true;
        }
        return false;
    } catch (err) {
        console.error("copyRichTextToClipboard error:", err);
        // Fallback to simple text copy if possible
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(plainText);
                return true;
            }
        } catch (fallbackErr) {
            console.error("Clipboard fallback error:", fallbackErr);
        }
        return false;
    }
};

